import React, { Dispatch, SetStateAction } from "react";
import gql from "graphql-tag";
import { Box, Modal, Flex, Text, TouchableOpacity, ChevronLeft } from "@atoms";
import { useStoreAppValue } from "@hooks";
import { IntentCategoryEnum } from "@types";
import { FundsPageFundraiserTransfer } from "../FundsPageFundraiserTransfer";
import { FundsPageFundraiserTabs } from "../FundsPageFundraiserTabs";
import { FundsPageFundraiserList } from "../FundsPageFundraiserList";
// import { FundsPageFundraiserFragment } from "./__generated__/FundsPageFundraiserFragment";

// == Types ================================================================

interface IProps {
  id: string;
  name: string;
  isOpen: boolean;
  isLoading: boolean;
  setOpen: (isOpen: boolean) => void;
  campaign: FundsPageFundraiserFragment;
  activeFundraiserTab: IntentCategoryEnum;
  setFundraiserTab: Dispatch<SetStateAction<IntentCategoryEnum>>;
  isTransferOpen: boolean;
  queryVariables: $FixMe;
  setTransferOpen: (isTransferOpen: boolean) => void;
  selectedListItem: $FixMe;
  setSelectedListItem: (selectedListItem: string) => void;
  hasNextPage: boolean;
  onFetchMore: () => void;
}

// == Constants ============================================================

FundsPageFundraiser.fragments = gql`
  fragment FundsPageFundraiserFragment on Campaign {
    id
    ...FundsPageFundraiserListFragment
  }
  ${FundsPageFundraiserList.fragments}
`;

// == Component ============================================================

export function FundsPageFundraiser({
  id,
  isOpen,
  setOpen,
  campaign,
  name,
  activeFundraiserTab,
  setFundraiserTab,
  isTransferOpen,
  setTransferOpen,
  selectedListItem,
  setSelectedListItem,
  queryVariables,
  onFetchMore,
  hasNextPage,
}: IProps) {
  const {
    theme: {
      colors: { primary },
    },
  } = useStoreAppValue();

  const isDirectFundraiser = id === name; // comparing fundraiserID to campaignName - will be equal for injected Direct Fundraiser

  const renderContent = () => {
    if (isTransferOpen)
      return (
        <FundsPageFundraiserTransfer
          campaign={campaign}
          selectedListItem={selectedListItem}
          setOpen={setOpen}
          setTransferOpen={setTransferOpen}
        />
      );
    if (!isTransferOpen && !!campaign) {
      return (
        <>
          <FundsPageFundraiserTabs
            activeFundraiserTab={activeFundraiserTab}
            setFundraiserTab={setFundraiserTab}
          />
          <FundsPageFundraiserList
            activeFundraiserTab={activeFundraiserTab}
            campaign={campaign}
            hasNextPage={hasNextPage}
            isDirectFundraiser={isDirectFundraiser}
            setOpen={setOpen}
            setSelectedListItem={setSelectedListItem}
            setTransferOpen={setTransferOpen}
            onFetchMore={onFetchMore}
          />
        </>
      );
    }
    if (!isTransferOpen && !campaign)
      return (
        <FundsPageFundraiserTabs
          activeFundraiserTab={activeFundraiserTab}
          setFundraiserTab={setFundraiserTab}
        />
      );
    return null;
  };

  const renderHeader = () => {
    if (isTransferOpen) return `Transfer Funds`;
    if (isDirectFundraiser) return `${name} (Team)`;
    return name;
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={() => (isTransferOpen ? setTransferOpen(false) : setOpen(false))}
    >
      <Flex alignItems="baseline" borderBottom="lightGreyBg" justifyContent="flex-start">
        <Box padding={[2, 2, 3]} paddingLeft={3}>
          <TouchableOpacity
            onTouch={() => (isTransferOpen ? setTransferOpen(false) : setOpen(false))}
          >
            <ChevronLeft color={primary} fontSize={[2, 3, 4]} />
          </TouchableOpacity>
        </Box>

        <Box padding={[2, 2, 3]}>
          <Text color="black" fontSize={[2, 2, 3]}>
            {renderHeader()}
          </Text>
        </Box>
      </Flex>
      {renderContent()}
    </Modal>
  );
}

// == Styles ===============================================================
